import type {
  Channel,
  Event,
  LocalMessage,
  OwnUserResponse,
  UserResponse,
} from 'stream-chat';
import type { TChatMessage, TThread, TUnreadPeer } from '@/services/chat';

import { CONCIERGE_CHANNEL_PREFIX } from '@/constants/messages';

const toConciergeChannelID = (memberID: string): string => `${CONCIERGE_CHANNEL_PREFIX}${memberID}`;

const toPeerID = (channel: Channel, staffID: string): string => {
  const members = Object.values(channel.state.members);
  const peer = members.find(member => member.user_id !== staffID);

  return peer?.user_id ?? '';
};

const toThread = (channel: Channel, staffID: string): TThread => {
  const last = channel.state.messages[channel.state.messages.length - 1];

  return {
    channelID: channel.id ?? '',
    memberID: toPeerID(channel, staffID),
    lastMessage: last?.text ?? '',
    lastMessageAt: last?.created_at?.toString() ?? null,
    unreadCount: channel.countUnread(),
  };
};

const toUnreadPeers = (channels: readonly Channel[], staffID: string): TUnreadPeer[] => channels
  .map(channel => toThread(channel, staffID))
  .filter(thread => thread.unreadCount > 0 && thread.memberID.length > 0)
  .map(thread => ({ memberID: thread.memberID, lastMessageAt: thread.lastMessageAt }));

const toChatMessage = (message: LocalMessage, staffID: string): TChatMessage => ({
  id: message.id,
  authorID: message.user?.id ?? '',
  body: message.text ?? '',
  sentAt: message.created_at?.toString() ?? '',
  isOwn: message.user?.id === staffID,
});

const toUnreadCount = (user?: UserResponse | OwnUserResponse | null): number => {
  if (user && 'total_unread_count' in user && typeof user.total_unread_count === 'number') {
    return user.total_unread_count;
  }

  return 0;
};

const toEventUnread = (event: Event, fallback: number): number => {
  if (typeof event.total_unread_count === 'number') {
    return event.total_unread_count;
  }

  return fallback;
};

export {
  toChatMessage,
  toConciergeChannelID,
  toEventUnread,
  toUnreadCount,
  toPeerID,
  toThread,
  toUnreadPeers,
};
